import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Orbit } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import {
  calculatePortfolioProjections,
  generateTimeSeriesData,
} from '../data/mockData';

/**
 * Split-Flap digit — animates a number rolling with a flip effect.
 */
const SplitFlap = ({ value, className = '', digits = 0 }) => {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef();
  const startRef = useRef();
  const fromRef = useRef(0);

  useEffect(() => {
    cancelAnimationFrame(rafRef.current);
    const duration = 1100;
    fromRef.current = display;
    startRef.current = null;

    const step = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(1, elapsed / duration);
      // easeOutExpo for a satisfying flap-settle effect
      const eased = 1 - Math.pow(2, -10 * t);
      const current = fromRef.current + (value - fromRef.current) * eased;
      setDisplay(current);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        setDisplay(value);
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line
  }, [value]);

  // Format number with thousand separators
  const formatted = useMemo(() => {
    if (Math.abs(display) >= 1e6) return `$${(display / 1e6).toFixed(2)}M`;
    if (Math.abs(display) >= 1e3) return `$${(display / 1e3).toFixed(1)}K`;
    return `$${Math.round(display)}`;
  }, [display]);

  // Split into digits/chars for flip animation
  return (
    <span className={`inline-flex ${className}`} data-testid="split-flap">
      {formatted.split('').map((ch, i) => (
        <span
          key={`${ch}-${i}-${Math.floor(display)}`}
          className="split-flap-char inline-block relative"
          style={{
            minWidth: ch === '.' || ch === ',' ? '0.35em' : ch === '$' ? '0.55em' : '0.65em',
            textAlign: 'center',
          }}
        >
          {ch}
        </span>
      ))}
    </span>
  );
};

/**
 * Stat with flip animation for percentage
 */
const FlipPercent = ({ value, color }) => {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef();

  useEffect(() => {
    cancelAnimationFrame(rafRef.current);
    const from = display;
    const duration = 1100;
    let startTs = null;
    const step = (ts) => {
      if (!startTs) startTs = ts;
      const t = Math.min(1, (ts - startTs) / duration);
      const eased = 1 - Math.pow(2, -10 * t);
      setDisplay(from + (value - from) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
      else setDisplay(value);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line
  }, [value]);

  return (
    <span style={{ color, textShadow: `0 0 10px ${color}80` }}>
      {display >= 0 ? '+' : ''}
      {display.toFixed(1)}%
    </span>
  );
};

const ScenarioMultiverse = ({
  totalCapital,
  monthlyContribution,
  allocations,
  aiSplit,
  rates,
}) => {
  const { t } = useLanguage();
  const [animKey, setAnimKey] = useState(0);

  // Force stagger animation to replay when config changes
  useEffect(() => {
    setAnimKey((k) => k + 1);
  }, [totalCapital, monthlyContribution, allocations, aiSplit, rates]);

  // Build adjusted rate sets
  const scaleRates = (factor) => {
    const out = {};
    Object.entries(rates).forEach(([k, v]) => {
      out[k] = { ...v, rate: (v.rate || 0) * factor };
    });
    return out;
  };

  const scenarios = useMemo(() => {
    const pessimistic = scaleRates(0.5);
    const realistic = rates;
    const optimistic = scaleRates(1.5);

    const days = 1825; // 5 years
    const build = (r) => {
      const series = generateTimeSeriesData(
        totalCapital,
        allocations,
        aiSplit,
        r,
        days,
        monthlyContribution
      );
      const proj = calculatePortfolioProjections(
        totalCapital,
        allocations,
        aiSplit,
        r,
        monthlyContribution
      );
      return { series, proj };
    };

    return {
      pessimistic: build(pessimistic),
      realistic: build(realistic),
      optimistic: build(optimistic),
    };
    // eslint-disable-next-line
  }, [totalCapital, monthlyContribution, allocations, aiSplit, rates]);

  // Chart geometry
  const W = 900;
  const H = 320;
  const PAD = { l: 60, r: 20, t: 30, b: 40 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;

  // Determine global min/max
  const allPoints = [
    ...scenarios.pessimistic.series.map((p) => p.value),
    ...scenarios.realistic.series.map((p) => p.value),
    ...scenarios.optimistic.series.map((p) => p.value),
  ];
  const yMin = Math.min(...allPoints, totalCapital);
  const yMax = Math.max(...allPoints);
  const yRange = yMax - yMin || 1;

  const maxDays = 1825;
  const xScale = (day) => PAD.l + (day / maxDays) * innerW;
  const yScale = (value) =>
    PAD.t + innerH - ((value - yMin) / yRange) * innerH;

  const toPath = (series) => {
    if (!series.length) return '';
    return series
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xScale(p.day)} ${yScale(p.value)}`)
      .join(' ');
  };

  const formatShortMoney = (amount) => {
    if (Math.abs(amount) >= 1e6) return `$${(amount / 1e6).toFixed(1)}M`;
    if (Math.abs(amount) >= 1e3) return `$${(amount / 1e3).toFixed(1)}K`;
    return `$${Math.round(amount)}`;
  };

  const SCENARIOS_CONFIG = [
    {
      key: 'pessimistic',
      label: t('scenarioPessimistic'),
      subLabel: '−50%',
      color: '#ff5577',
      glow: 'rgba(255,85,119,0.5)',
      dash: '4 4',
      width: 2,
      delay: 0.3,
    },
    {
      key: 'realistic',
      label: t('scenarioRealistic'),
      subLabel: t('scenarioCurrent'),
      color: '#00f4ff',
      glow: 'rgba(0,244,255,0.6)',
      dash: '0',
      width: 3,
      delay: 0,
    },
    {
      key: 'optimistic',
      label: t('scenarioOptimistic'),
      subLabel: '+50%',
      color: '#ffd700',
      glow: 'rgba(255,215,0,0.55)',
      dash: '0',
      width: 2,
      delay: 0.6,
    },
  ];

  // Generate X-axis tick marks for 1Y, 3Y, 5Y
  const xTicks = [
    { day: 365, label: '1Y' },
    { day: 1095, label: '3Y' },
    { day: 1825, label: '5Y' },
  ];

  // Y-axis tick values (5 gridlines)
  const yTicks = useMemo(() => {
    const ticks = [];
    for (let i = 0; i <= 4; i++) {
      const value = yMin + (yRange * i) / 4;
      ticks.push(value);
    }
    return ticks;
  }, [yMin, yRange]);

  return (
    <Card
      className="bg-[#040816] border-[#0b1c38]"
      data-testid="scenario-multiverse-card"
    >
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-[#00f4ff] flex items-center gap-2 text-lg">
              <Orbit className="w-5 h-5" />
              {t('multiverseTitle')}
            </CardTitle>
            <p className="text-xs text-[#9EB4C0] mt-1 max-w-2xl">
              {t('multiverseSubtitle')}
            </p>
          </div>
          <div className="flex items-center gap-4 text-[11px] text-[#9EB4C0]">
            {SCENARIOS_CONFIG.map((s) => (
              <div key={s.key} className="flex items-center gap-1.5">
                <span
                  className="inline-block w-5 h-[2px] rounded-full"
                  style={{
                    backgroundColor: s.color,
                    boxShadow: `0 0 8px ${s.color}`,
                  }}
                />
                {s.label}
                <span className="text-[#556475] text-[10px]">{s.subLabel}</span>
              </div>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Chart */}
        <div className="w-full overflow-x-auto">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="w-full"
            style={{ minWidth: '720px', maxHeight: '400px' }}
            data-testid="multiverse-svg"
          >
            <defs>
              {SCENARIOS_CONFIG.map((s) => (
                <linearGradient
                  key={`grad-${s.key}`}
                  id={`multi-grad-${s.key}`}
                  x1="0%"
                  x2="0%"
                  y1="0%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor={s.color} stopOpacity="0.25" />
                  <stop offset="100%" stopColor={s.color} stopOpacity="0" />
                </linearGradient>
              ))}
            </defs>

            {/* Grid lines */}
            {yTicks.map((v, i) => (
              <g key={`yt-${i}`}>
                <line
                  x1={PAD.l}
                  x2={W - PAD.r}
                  y1={yScale(v)}
                  y2={yScale(v)}
                  stroke="#0b1c38"
                  strokeWidth={0.5}
                  strokeDasharray="2 4"
                />
                <text
                  x={PAD.l - 8}
                  y={yScale(v) + 4}
                  fill="#556475"
                  fontSize="10"
                  textAnchor="end"
                >
                  {formatShortMoney(v)}
                </text>
              </g>
            ))}

            {/* X-axis ticks */}
            {xTicks.map((xt) => (
              <g key={`xt-${xt.day}`}>
                <line
                  x1={xScale(xt.day)}
                  x2={xScale(xt.day)}
                  y1={PAD.t}
                  y2={H - PAD.b}
                  stroke="#0b1c38"
                  strokeWidth={0.5}
                  strokeDasharray="2 4"
                />
                <text
                  x={xScale(xt.day)}
                  y={H - PAD.b + 16}
                  fill="#9EB4C0"
                  fontSize="11"
                  textAnchor="middle"
                  fontWeight={600}
                >
                  {xt.label}
                </text>
              </g>
            ))}

            {/* Area fills (optional, subtle) */}
            {SCENARIOS_CONFIG.map((s) => {
              const series = scenarios[s.key].series;
              if (!series.length) return null;
              const topPath = toPath(series);
              const areaPath = `${topPath} L ${xScale(
                series[series.length - 1].day
              )} ${H - PAD.b} L ${xScale(series[0].day)} ${H - PAD.b} Z`;
              return (
                <path
                  key={`area-${s.key}-${animKey}`}
                  d={areaPath}
                  fill={`url(#multi-grad-${s.key})`}
                  opacity={0.6}
                  style={{
                    animation: `fadeInArea 2s ease ${s.delay + 1}s both`,
                  }}
                />
              );
            })}

            {/* Animated lines (stagger draw) */}
            {SCENARIOS_CONFIG.map((s) => {
              const series = scenarios[s.key].series;
              if (!series.length) return null;
              return (
                <path
                  key={`line-${s.key}-${animKey}`}
                  d={toPath(series)}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={s.width}
                  strokeDasharray={s.dash}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    filter: `drop-shadow(0 0 6px ${s.glow})`,
                    strokeDashoffset: 3000,
                    animation: `drawLine 2s ease ${s.delay}s forwards`,
                  }}
                />
              );
            })}

            {/* End point markers with labels */}
            {SCENARIOS_CONFIG.map((s, idx) => {
              const series = scenarios[s.key].series;
              if (!series.length) return null;
              const last = series[series.length - 1];
              return (
                <g
                  key={`end-${s.key}-${animKey}`}
                  style={{
                    opacity: 0,
                    animation: `fadeIn 0.5s ease ${s.delay + 1.8}s forwards`,
                  }}
                >
                  <circle
                    cx={xScale(last.day)}
                    cy={yScale(last.value)}
                    r={4}
                    fill={s.color}
                    style={{ filter: `drop-shadow(0 0 6px ${s.glow})` }}
                  />
                  <circle
                    cx={xScale(last.day)}
                    cy={yScale(last.value)}
                    r={8}
                    fill="none"
                    stroke={s.color}
                    strokeOpacity={0.5}
                    strokeWidth={1.5}
                  >
                    <animate
                      attributeName="r"
                      values="6;12;6"
                      dur="2.4s"
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="opacity"
                      values="0.6;0;0.6"
                      dur="2.4s"
                      repeatCount="indefinite"
                    />
                  </circle>
                </g>
              );
            })}
          </svg>
        </div>

        <style>{`
          @keyframes drawLine {
            from { stroke-dashoffset: 3000; opacity: 0.3; }
            to   { stroke-dashoffset: 0;    opacity: 1; }
          }
          @keyframes fadeInArea {
            from { opacity: 0; }
            to   { opacity: 0.6; }
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.5); }
            to   { opacity: 1; transform: scale(1); }
          }
          @keyframes flipIn {
            0%   { transform: rotateX(90deg); opacity: 0; }
            60%  { transform: rotateX(-12deg); opacity: 1; }
            100% { transform: rotateX(0deg); }
          }
          .split-flap-char {
            background: linear-gradient(180deg, #0a1728 0%, #040816 50%, #0a1728 100%);
            border: 1px solid rgba(0,244,255,0.15);
            border-radius: 4px;
            padding: 6px 2px;
            margin: 0 1px;
            box-shadow: inset 0 0 8px rgba(0,0,0,0.6), 0 2px 6px rgba(0,0,0,0.3);
            animation: flipIn 0.45s ease both;
            transform-style: preserve-3d;
            backface-visibility: hidden;
          }
          .split-flap-char::before {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(180deg, transparent 48%, rgba(0,0,0,0.45) 50%, transparent 52%);
            pointer-events: none;
            border-radius: 4px;
          }
        `}</style>

        {/* Split-flap metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          {SCENARIOS_CONFIG.map((s, idx) => {
            const proj5y = scenarios[s.key].proj[1825] || {};
            const fv = proj5y.total || 0;
            const roi = proj5y.roi || 0;
            const netGain = proj5y.netGain || 0;
            return (
              <div
                key={`stat-${s.key}`}
                className="relative rounded-xl p-5 overflow-hidden"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(11,28,56,0.65) 0%, rgba(4,8,22,0.9) 100%)',
                  border: `1px solid ${s.color}44`,
                  boxShadow: `0 10px 30px -15px ${s.glow}, inset 0 0 0 1px ${s.color}22`,
                }}
                data-testid={`multiverse-stat-${s.key}`}
              >
                {/* Corner glow */}
                <div
                  className="absolute -top-10 -right-10 w-32 h-32 rounded-full pointer-events-none"
                  style={{
                    background: `radial-gradient(circle, ${s.color}30, transparent 70%)`,
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className="text-[10px] font-bold tracking-[0.25em] uppercase"
                      style={{ color: s.color }}
                    >
                      {s.label}
                    </span>
                    <span
                      className="text-[9px] font-mono px-1.5 py-0.5 rounded"
                      style={{
                        color: s.color,
                        backgroundColor: `${s.color}18`,
                        border: `1px solid ${s.color}33`,
                      }}
                    >
                      {s.subLabel}
                    </span>
                  </div>

                  {/* FV — Split Flap */}
                  <div className="mt-3">
                    <div className="text-[9px] tracking-[0.2em] uppercase text-[#556475] mb-1">
                      {t('multiverseFV5y')}
                    </div>
                    <div
                      className="text-2xl md:text-3xl font-black text-white"
                      style={{
                        fontFamily:
                          'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
                        letterSpacing: '0.02em',
                        filter: `drop-shadow(0 0 6px ${s.glow})`,
                      }}
                      key={`fv-${animKey}`}
                    >
                      <SplitFlap value={fv} />
                    </div>
                  </div>

                  {/* ROI — Flip Percent */}
                  <div className="mt-3 flex items-baseline justify-between">
                    <div>
                      <div className="text-[9px] tracking-[0.2em] uppercase text-[#556475]">
                        ROI
                      </div>
                      <div
                        className="text-xl font-extrabold"
                        style={{ fontFamily: 'ui-monospace, monospace' }}
                        key={`roi-${animKey}-${s.key}`}
                      >
                        <FlipPercent value={roi} color={s.color} />
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[9px] tracking-[0.2em] uppercase text-[#556475]">
                        {t('multiverseNetGain')}
                      </div>
                      <div
                        className="text-sm font-bold text-white/80"
                        style={{ fontFamily: 'ui-monospace, monospace' }}
                      >
                        {formatShortMoney(netGain)}
                      </div>
                    </div>
                  </div>

                  {/* Progress bar comparing to realistic */}
                  <div className="mt-4">
                    <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-1000"
                        style={{
                          width: `${Math.min(
                            100,
                            (fv /
                              Math.max(
                                scenarios.optimistic.proj[1825]?.total || 1,
                                1
                              )) *
                              100
                          )}%`,
                          background: `linear-gradient(90deg, ${s.color}40 0%, ${s.color} 100%)`,
                          boxShadow: `0 0 8px ${s.glow}`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-4 text-[11px] text-center text-[#556475] italic">
          {t('multiverseDisclaimer')}
        </p>
      </CardContent>
    </Card>
  );
};

export default ScenarioMultiverse;
